import React, { useState, useMemo, useCallback } from 'react';
import {useDispatch} from "react-redux";
import { Card, Row, Col, Empty, Space, Layout, Divider, Typography, Table } from 'antd';
import Position from "../Position/Position";
import { sampleColors } from "../../../utils/colors";
import Placeholder from "../../../Components/Placeholder";
import { calculateOverlap, flattenEtfPositions, consolidateEtfPositions } from "../../../lib/etfTools";
import NivoHeatChart from "../../../charts/nivo/NivoHeatChart";
import { transformDataForNivoHeatMap } from "../../../charts/nivo/NivoHeatChart";
import XrayTable from "../../Xray/XrayTable";
import _ from 'lodash';
import NivoTreeChart from "../../../charts/nivo/NivoTreeChart";
import {getRandomColor} from "../../../utils/colorUtils";
import { stringMixin } from "../../../utils/strings";


const extractUniqueSectors = (positions)=>{
  const sectors = new Set();
  Object.values(positions).forEach(etf=>{
    etf.holdings.forEach(ticker=>{
      sectors.add(ticker.sector);
    });
  });
  return Array.from(sectors);
}



const SectorLegend = ({ uniqueSectors, sectorColors }) => {
  const gridStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, 70px)', // You can adjust this value as needed
    gap: '10px',
  };

  const itemStyle = {
    background: '#000',
    color: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    borderRadius: '4px',
    width: '70px',  // Ensure the square shape
    height: '70px', // Ensure the square shape
    wordWrap: 'wrap',
    fontSize: '9px',
      fontWeight: '400',
      fontFamily: "Inter, sans-serif",
    padding: '3px',
    textTransform: 'uppercase'
  };

  return (
    <Card bordered={false} className={'gapless'}>
      <div style={gridStyle} className={'pl-3'}>
        {uniqueSectors.map((sector, idx) => (
          <div key={idx} style={{ ...itemStyle, background: sectorColors[sector] }}>
            {sector}
          </div>
        ))}
      </div>
    </Card>
  );
};


const PositionCollection = ({ selectedTickers, positions, etfWeights, onRemove, filters, etfColors,
                                mode='matrix' }) => {
  let [checkboxProps, setCheckboxProps] = useState({ isChecked: false, symbol: null, tickerList: [] });
  const uniqueSectors = useMemo(() => extractUniqueSectors(positions), [positions]);
  const randomColors = useMemo(() => sampleColors(uniqueSectors.length, 'blue'), [uniqueSectors]);
  const sectorColors = useMemo(() => uniqueSectors.reduce((acc, sector, idx) => {
        acc[sector] = randomColors[idx];
        return acc;
  }, {}), [uniqueSectors, randomColors]);
  const flattenedPositions = useMemo(() => positions ? flattenEtfPositions(Object.values(positions), etfWeights) : [], [positions]);
  const overlapData = useMemo(() => flattenedPositions ? calculateOverlap(flattenedPositions) : null, [flattenedPositions]);
  const consolidatedPositions = flattenedPositions ? consolidateEtfPositions(_.cloneDeep(flattenedPositions)) : null;
  const treeData = {name: "ETF Weights", children: Object.keys(positions).map(x=>{
      return {name: positions[x]?.name, value: (etfWeights?.[x]*100).toFixed(1) ?? 0, color: getRandomColor()}
      })
  };

  const onCheck = useCallback((etfSymbol) => {
    setCheckboxProps(prevProps => {
        if (prevProps.symbol === etfSymbol) {
            // Toggle the checkbox if the same ETF is clicked
            return {
                isChecked: !prevProps.isChecked,
                symbol: prevProps.isChecked ? null : etfSymbol,
                tickerList: prevProps.isChecked ? [] : positions[etfSymbol].holdings
            };
        } else {
            // Select the new ETF if a different one is clicked
            return {
                isChecked: true,
                symbol: etfSymbol,
                tickerList: positions[etfSymbol].holdings
            };
        }
    });
  }, [positions]);

  const columns = [
      {
          title: "Name",
            dataIndex: 'name',
            key: 'name',
          width: 50,
          render: (_, record)=>(
              <div>
                  <div style={{textTransform: 'uppercase', color: etfColors[record.symbol], fontWeight: '600'}}>{record.symbol}</div>
                  <div style={{color: etfColors[record.symbol]}}>{record.specs.issuer}</div>
              </div>
          )
      },
      {
          title: "Exp",
            dataIndex: ['specs', 'expense'],
            key: 'expense',
          render: (value) => `${(value * 100).toFixed(2)}%`
      },
      {
          title: "Corr",
          dataIndex: ['stats', 'benchmarks', 'spy', 'corr_1_yr'],
            key: 'corrSPY',
            render: (value, record) => record.symbol === 'spy' ? '--' : `${(value * 100).toFixed(2)}%`

      },
      {
          title: "AUM",
          dataIndex: ['specs', 'aum'],
            key: 'aum',
            render: (value) => stringMixin.largeNumber(value)
      }
  ]


    return (

        <>
            {mode==='matrix' && (
            <Layout>
              <Layout.Sider width={300} style={{background: "#fff", padding: '20px 10px'}}>
                  <div className={'shadow-sm'} style={{maxHeight: '80vh', overflowY: 'scroll'}}>
                      <Table className={'small'} dataSource={Object.values(positions)} columns={columns} pagination={false}/>
                      <div style={{height: 300, width: '100%', padding: '0 20px'}}>
                          <NivoHeatChart
                              minValue={0}
                              maxValue={100}
                              data={transformDataForNivoHeatMap(overlapData)} keys={overlapData.map(x=>x.x)}
                              labelColor={'#FCFF94'}
                              labelSize={selectedTickers.length > 3 ? 10: 11}
                              showUpperAxis={false}
                          />
                      </div>
                     <Divider/>
                      {Object.values(etfWeights).some(x=>x>0) && (
                          <>
                              <Typography.Title style={{paddingLeft: 30}} level={5}>ETF Weights</Typography.Title>
                              <div style={{height: 300, width: '100%'}}>
                                  <NivoTreeChart data={treeData} initialMode={'slice'} />
                              </div>
                              <Divider/>
                          </>
                      )}
                      <Typography.Title style={{paddingLeft: 30}} level={5}>Sector Legend</Typography.Title>
                      <SectorLegend uniqueSectors={uniqueSectors} sectorColors={sectorColors}/>
                  </div>
              </Layout.Sider>
              <Layout.Content>
                  <Row gutter={[16, 16]} wrap={true} justify={'start'}>
                      {selectedTickers.map(ticker => (
                        <Col key={ticker.symbol} xs={24} lg={12} xl={8}>
                            {positions[ticker.symbol] ? (
                                <Position
                                    etf={positions[ticker.symbol]}
                                    onRemove={() => onRemove(ticker)}
                                    checkboxProps={checkboxProps}
                                    onCheck={() => onCheck(ticker.symbol)}
                                    sectorColors={sectorColors}
                                    filters={filters}
                                    etfColor={etfColors[ticker.symbol]}
                                    weight={etfWeights?.[ticker.symbol] || 0}
                                  />
                            ) : <Placeholder text={'No ETF Data'}/>}
                        </Col>
                      ))}
                    </Row>
              </Layout.Content>
          </Layout>
            )}
            {mode==='consolidated' && (
                consolidatedPositions ? (
                    <>
                        <Divider/>
                        <XrayTable data={consolidatedPositions}
                                   etfColors={etfColors}
                                   sectorColors={sectorColors}
                        />
                    </>
                    )
                    :
                    <Empty/>
            )}
            {}
        </>
    );
};

export default PositionCollection;
